/**
 * Constrained bottom-right pocket for the Zendesk chat FAB.
 *
 * Full-viewport drag parked the bubble on form fields and looked like a
 * bug. Keep it in the corner; allow a small nudge so users can uncover
 * pagination / footer actions.
 */

import type { ZendeskLauncherPos } from '@/lib/zendesk';

/** FAB diameter in px. */
export const ZENDESK_FAB_SIZE_PX = 52;

/** Minimum gap from the viewport edge. */
export const ZENDESK_FAB_EDGE_PAD_PX = 12;

/**
 * How far the FAB may travel from the bottom-right home, as a fraction of
 * the viewport. ~one button-width — enough to uncover a control, not roam.
 */
export const ZENDESK_FAB_DRAG_RANGE_RATIO = 0.04;

/** Floor so phones still have a usable nudge when 4% of the viewport is tiny. */
export const ZENDESK_FAB_MIN_DRAG_TRAVEL_PX = 32;

/** Cap so ultrawide desktops cannot turn 4% into a mid-page roam. */
export const ZENDESK_FAB_MAX_DRAG_TRAVEL_PX = 56;

/** Ignore click if the pointer moved farther than this (px) during a drag. */
export const ZENDESK_FAB_DRAG_THRESHOLD_PX = 6;

/** How a captured pointer should resolve when it ends. */
export type ZendeskFabPointerEnd = 'click' | 'drag' | 'cancel';

/**
 * Classify a FAB pointer end. Cancel is never a click — browsers often
 * fire it with 0,0 coords when the button moves under capture, which
 * would look like a huge drag and swallow the open.
 *
 * @param type - Native pointer event type
 * @param dx - clientX delta from pointerdown
 * @param dy - clientY delta from pointerdown
 */
export function classifyZendeskFabPointerEnd(
  type: 'pointerup' | 'pointercancel',
  dx: number,
  dy: number,
): ZendeskFabPointerEnd {
  if (type === 'pointercancel') return 'cancel';
  if (Math.abs(dx) > ZENDESK_FAB_DRAG_THRESHOLD_PX || Math.abs(dy) > ZENDESK_FAB_DRAG_THRESHOLD_PX) {
    return 'drag';
  }
  return 'click';
}

type ViewportSize = {
  width: number;
  height: number;
};

/**
 * Clamp a computed travel distance into the min/max nudge window.
 */
function clampTravel(distance: number): number {
  return Math.min(
    ZENDESK_FAB_MAX_DRAG_TRAVEL_PX,
    Math.max(ZENDESK_FAB_MIN_DRAG_TRAVEL_PX, distance),
  );
}

/**
 * Read the current viewport, or a zero box when `window` is unavailable.
 */
function currentViewport(): ViewportSize {
  if (typeof window === 'undefined') {
    return { width: 0, height: 0 };
  }
  return { width: window.innerWidth, height: window.innerHeight };
}

/**
 * Bottom-right pocket the FAB is allowed to occupy.
 *
 * @param viewport - Defaults to the live window size.
 */
export function zendeskLauncherPocket(
  viewport: ViewportSize = currentViewport(),
): { minLeft: number; maxLeft: number; minTop: number; maxTop: number } {
  const maxLeft = Math.max(
    ZENDESK_FAB_EDGE_PAD_PX,
    viewport.width - ZENDESK_FAB_SIZE_PX - ZENDESK_FAB_EDGE_PAD_PX,
  );
  const maxTop = Math.max(
    ZENDESK_FAB_EDGE_PAD_PX,
    viewport.height - ZENDESK_FAB_SIZE_PX - ZENDESK_FAB_EDGE_PAD_PX,
  );
  const travelX = clampTravel(Math.round(viewport.width * ZENDESK_FAB_DRAG_RANGE_RATIO));
  const travelY = clampTravel(Math.round(viewport.height * ZENDESK_FAB_DRAG_RANGE_RATIO));
  return {
    minLeft: Math.max(ZENDESK_FAB_EDGE_PAD_PX, maxLeft - travelX),
    maxLeft,
    minTop: Math.max(ZENDESK_FAB_EDGE_PAD_PX, maxTop - travelY),
    maxTop,
  };
}

/**
 * Default FAB position: geometric center of the bottom-right pocket.
 *
 * Home stays here until the user drags. A pinned drag is stored separately
 * and only clamped on resize — it does not snap back to center.
 *
 * @param viewport - Defaults to the live window size.
 */
export function defaultZendeskLauncherPos(
  viewport: ViewportSize = currentViewport(),
): ZendeskLauncherPos {
  const pocket = zendeskLauncherPocket(viewport);
  return {
    left: Math.round((pocket.minLeft + pocket.maxLeft) / 2),
    top: Math.round((pocket.minTop + pocket.maxTop) / 2),
  };
}

/**
 * Clamp a FAB position into the bottom-right drag pocket.
 *
 * Stale mid-page positions (from the old free-roam drag) snap back into
 * the corner so the bubble cannot sit on form fields.
 *
 * @param pos - Candidate top-left in viewport px.
 * @param viewport - Defaults to the live window size.
 */
export function clampZendeskLauncherPos(
  pos: ZendeskLauncherPos,
  viewport: ViewportSize = currentViewport(),
): ZendeskLauncherPos {
  const pocket = zendeskLauncherPocket(viewport);
  return {
    left: Math.min(pocket.maxLeft, Math.max(pocket.minLeft, pos.left)),
    top: Math.min(pocket.maxTop, Math.max(pocket.minTop, pos.top)),
  };
}
